// frontend/src/components/dedup/CampaignSelector.tsx
import { getDedupUrl, authFetch } from '../../config';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert
} from '@mui/material';
import { Add as AddIcon, Campaign as CampaignIcon } from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';

interface Campaign {
  id: number;
  name: string;
  description?: string;
  status?: string;
  source_count?: number;
  created_at?: string;
}

interface Props {
  selectedCampaign: Campaign | null;
  onSelect: (campaign: Campaign) => void;
}

export default function CampaignSelector({ selectedCampaign, onSelect }: Props) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const response = await authFetch(getDedupUrl('/campaigns'));
      const result = await response.json();
      if (result.success) {
        setCampaigns(result.campaigns);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) {
      alert('Bitte geben Sie einen Kampagnennamen ein');
      return;
    }

    try {
      const response = await authFetch(getDedupUrl('/campaigns'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCampaign)
      });

      const result = await response.json();
      if (result.success) {
        await loadCampaigns();
        onSelect(result.campaign);
        setOpenDialog(false);
        setNewCampaign({ name: '', description: '' });
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert('Fehler beim Erstellen der Kampagne');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">
          Kampagne auswählen oder erstellen
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Neue Kampagne
        </Button>
      </Box>

      {campaigns.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CampaignIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Noch keine Kampagnen vorhanden
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Erstellen Sie Ihre erste Deduplizierungs-Kampagne
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Erste Kampagne erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <RadioGroup
          value={selectedCampaign?.id || ''}
          onChange={(e) => {
            const campaign = campaigns.find(c => c.id === Number(e.target.value));
            if (campaign) onSelect(campaign);
          }}
        >
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2 
          }}>
            {campaigns.map((campaign) => (
              <Card
                key={campaign.id}
                sx={{
                  cursor: 'pointer',
                  border: selectedCampaign?.id === campaign.id ? '2px solid' : '1px solid',
                  borderColor: selectedCampaign?.id === campaign.id ? 'primary.main' : 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 2
                  }
                }}
                onClick={() => onSelect(campaign)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <FormControlLabel
                      value={campaign.id}
                      control={<Radio />}
                      label=""
                      sx={{ m: 0 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {campaign.name}
                      </Typography>
                      {campaign.description && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {campaign.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                        <Chip 
                          label={`${campaign.source_count || 0} Quellen`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        {campaign.status && (
                          <Chip 
                            label={campaign.status}
                            size="small"
                            color={campaign.status === 'completed' ? 'success' : 'default'}
                          />
                        )}
                      </Box>
                      {campaign.created_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Erstellt: {formatDate(campaign.created_at)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </RadioGroup>
      )}

      {selectedCampaign && (
        <Alert severity="success" sx={{ mt: 3 }}>
          Ausgewählte Kampagne: <strong>{selectedCampaign.name}</strong>
        </Alert>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Kampagne erstellen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Kampagnenname *"
              value={newCampaign.name}
              onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="z.B. Neukunden Q4 2024"
            />
            <TextField
              fullWidth
              label="Beschreibung"
              value={newCampaign.description}
              onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
              multiline
              rows={3}
              placeholder="Optionale Beschreibung der Kampagne"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Abbrechen</Button>
          <Button onClick={handleCreateCampaign} variant="contained">
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}